import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBusinessContext } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useSearchParams } from 'react-router-dom';
import api, { getAssessment, getPersonalAssessment, getWealthGap, getValueGap, getProfitGap, getBusinessProfile, saveBusinessProfile } from '../services/api';
import { Sparkles, User, Building2, Save } from 'lucide-react';
import BusinessEnvisioning from '../components/envisioning/BusinessEnvisioning';
import PersonalEnvisioning from '../components/envisioning/PersonalEnvisioning';
import ViewerBanner from '../components/ViewerBanner';

const EnvisioningDashboard = () => {
  const { selectedBusiness } = useBusinessContext();
  const { isViewer } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'business'); // 'business' or 'personal'
  const [businessData, setBusinessData] = useState(null);
  const [personalData, setPersonalData] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [personalAssessment, setPersonalAssessment] = useState(null);
  const [wealthGap, setWealthGap] = useState(null);
  const [valueGap, setValueGap] = useState(null);
  const [profitGap, setProfitGap] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadEnvisioningData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness]);

  const loadEnvisioningData = async () => {
    try {
      setLoading(true);

      // Load business envisioning, personal envisioning, assessment data, gap data, and business profile
      const [businessRes, personalRes, assessmentRes, personalAssessmentRes, wealthGapRes, valueGapRes, profitGapRes, profileRes] = await Promise.all([
        api.get('/business-envisioning'),
        api.get('/personal-envisioning'),
        getAssessment(),
        getPersonalAssessment(),
        getWealthGap(),
        getValueGap(),
        getProfitGap(),
        getBusinessProfile()
      ]);

      setBusinessData(businessRes.data);
      setPersonalData(personalRes.data);

      // Store business profile data if available
      if (profileRes.success && profileRes.data?.profile) {
        setProfileData(profileRes.data.profile);
      } else {
      }

      // Store assessment data if available
      if (assessmentRes.success && assessmentRes.data) {
        setAssessmentData(assessmentRes.data);
      } else {
      }

      // Store personal assessment data if available
      if (personalAssessmentRes.success && personalAssessmentRes.data) {
        setPersonalAssessment(personalAssessmentRes.data);
      } else {
      }

      // Store wealth gap data if available
      if (wealthGapRes.success && wealthGapRes.data.wealth_gap) {
        setWealthGap(wealthGapRes.data.wealth_gap);
      }

      // Store value gap data if available
      if (valueGapRes.success && valueGapRes.data.value_gap) {
        setValueGap(valueGapRes.data.value_gap);
      }

      // Store profit gap data if available
      if (profitGapRes.success && profitGapRes.data.profit_gap) {
        setProfitGap(profitGapRes.data.profit_gap);
      }
    } catch (error) {
      console.error('Error loading envisioning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSave = async (data) => {
    try {
      setSaving(true);
      const response = await api.post('/business-envisioning', data);
      setBusinessData(response.data);
      setLastSaved(new Date());
      return response.data;
    } catch (error) {
      console.error('Error saving business envisioning:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handlePersonalSave = async (data) => {
    try {
      setSaving(true);
      const response = await api.post('/personal-envisioning', data);
      setPersonalData(response.data);
      setLastSaved(new Date());
      return response.data;
    } catch (error) {
      console.error('Error saving personal envisioning:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleProfileSave = async (profileUpdates) => {
    try {
      setSaving(true);
      const response = await saveBusinessProfile(profileUpdates);
      if (response.success && response.data?.profile) {
        setProfileData(response.data.profile);
        setLastSaved(new Date());
        return response.data.profile;
      }
      throw new Error(response.error || 'Failed to save profile');
    } catch (error) {
      console.error('Error saving business profile:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading envisioning data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Viewer Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        <ViewerBanner />
      </div>

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Envisioning</h1>
                <p className="text-xs sm:text-sm text-gray-500">
                  Clarify your 3–10 year vision for business and life beyond exit
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:space-x-4 w-full sm:w-auto">
              {lastSaved && (
                <div className="text-sm text-gray-500">
                  Last saved: {lastSaved.toLocaleTimeString()}
                </div>
              )}
              {saving && (
                <div className="flex items-center text-sm text-indigo-600">
                  <Save className="w-4 h-4 mr-1 animate-pulse" />
                  Saving...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('business');
                setSearchParams({ tab: 'business' });
              }}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'business'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center space-x-2">
                <Building2 className="w-5 h-5" />
                <span>Business Envisioning</span>
              </div>
            </button>

            <button
              onClick={() => {
                setActiveTab('personal');
                setSearchParams({ tab: 'personal' });
              }}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === 'personal'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>Personal Envisioning</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'business' ? (
          <BusinessEnvisioning
            data={businessData}
            assessmentData={assessmentData}
            wealthGap={wealthGap}
            valueGap={valueGap}
            profitGap={profitGap}
            profileData={profileData}
            onSave={handleBusinessSave}
            onProfileSave={handleProfileSave}
            saving={saving}
            isViewer={isViewer}
          />
        ) : (
          <PersonalEnvisioning
            data={personalData}
            personalAssessment={personalAssessment}
            wealthGap={wealthGap}
            profileData={profileData}
            onSave={handlePersonalSave}
            saving={saving}
            isViewer={isViewer}
          />
        )}
      </div>
    </div>
  );
};

export default EnvisioningDashboard;
